import { axiosService } from "@/services/AxiosService";



/**
 * @typedef {Object} Food
 * @property {number} id - ID of the food record.
 * @property {string} name - Name of the food.
 */

/**
 * @typedef {Object} FoodType
 * @property {number} id - ID of the food type record.
 * @property {string} name - Name of the food type.
 */

/**
 * @typedef {Object} Producer
 * @property {number} id - ID of the producer record.
 * @property {string} name - Name of the producer.
 */

/**
 * @typedef {Object} HorseDiet
 * @property {number} id - ID of the diet record.
 * @property {number} horse_id - ID of the horse associated with the diet.
 * @property {number} food_id - ID of the food assigned to the diet.
 * @property {number} quantity - Quantity of food given.
 * @property {string} measure_unity - Unit of measurement (e.g., kg, lb).
 * @property {number} number_of_times_a_day - How many times a day the food is given.
 * @property {Date} from_date - Start date of the diet.
 * @property {Date} to_date - End date of the diet.
 */



/**
 * Servizio per gestire le informazioni dei cavalli.
 */
class HorsesDietService {

    abortController = null;

    constructor() {
        this.axiosInstance = axiosService.getInstance(); // Usa l'istanza di AxiosService
    }

    /**
     * 
     * @returns {Promise<FoodType[]>}
     * @throws {Error}
     */
    async getFoodTypes() {
        try {
            const response = await this.axiosInstance.post('/food-types/get-food-types');

            if (response.status !== 200) {
                throw new Error('Failed to fetch food types info');
            }

            /** @type {FoodType[]} */
            return response.data.data.food_types;
        } catch (error) {
            console.error('Error fetching food types info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Producer[]>}
     * @throws {Error}
     */
    async getProducers(food_type) {
        try {
            const response = await this.axiosInstance.post('/food-type-producers/get-food-type-producers-given-food-type-id', {food_type_id: food_type});

            if (response.status !== 200) {
                throw new Error('Failed to fetch food type producers info');
            }

            /** @type {Producer[]} */
            return response.data.data.food_type_producers;
        } catch (error) {
            console.error('Error fetching food type producers info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Food[]>}
     * @throws {Error}
     */
    async getFoods(producer) {
        try {
            const response = await this.axiosInstance.post('/foods/get-foods-given-food-type-producer-id', {food_type_producer_id: producer});

            if (response.status !== 200) {
                throw new Error('Failed to fetch foods info');
            }

            /** @type {Food[]} */
            return response.data.data.foods;
        } catch (error) {
            console.error('Error fetching foods info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * 
     * @returns {Promise<HorseDiet>}
     * @throws {Error}
     * status -> inprogress - future - executed
     */
    async getHorseDiets(page, search = '', params = {}, length = 10) {
        try {
            const response = await this.axiosInstance.post('/horse-diets/get-horse-diets-given-horse-id', {page, search, length, ...params});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse diets info');
            }

            /** @type {HorseDiet} */
            return response.data;
        } catch (error) {
            console.error('Error fetching horse diet info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

     /**
     * 
     * @returns {Promise<HorseDiet>} - 
     * @throws {Error} - 
     */
     async getHorseDietById(id) {
        try {
            const response = await this.axiosInstance.post('/horse-diets/get-horse-diet-given-id', {horse_diet_id: id});

            if (response.status !== 200) {
                throw new Error('Failed to fetch horse diet info');
            }

            /** @type {HorseDiet} */
            return response.data.data;
        } catch (error) {
            console.error('Error fetching horse diet info:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<HorseDiet>} 
     * @throws {Error} 
     */
    async saveHorseDiet(data) {
        try {
            
            const response = await this.axiosInstance.post('/horse-diets/save-horse-diet', data);

            if (response.status !== 200) {
                throw new Error('Failed to save horse diet data');
            }

            /** @type {HorseDiet} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving horse diet:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }


    /**
     * 
     * @returns {Promise<FoodType>} 
     * @throws {Error} 
     */
    async addFoodType(name) {
        try {
            
            const response = await this.axiosInstance.post('/foods/add-food-type', {name});

            if (response.status !== 200) {
                throw new Error('Failed to save food type data');
            }

            /** @type {FoodType} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving food type:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Producer>} 
     * @throws {Error} 
     */
    async addProducer(name, food_type_id) {
        try {
            
            const response = await this.axiosInstance.post('/foods/add-producer', {name, food_type_id});

            if (response.status !== 200) {
                throw new Error('Failed to save producer data');
            }

            /** @type {Producer} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving producer:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    /**
     * 
     * @returns {Promise<Food>} 
     * @throws {Error} 
     */
    async addFood(name, food_type_producer_id) {
        try {
            
            const response = await this.axiosInstance.post('/foods/add-food', {name, food_type_producer_id});

            if (response.status !== 200) {
                throw new Error('Failed to save food data');
            }

            /** @type {Food} */
            return response.data.data.result;
        } catch (error) {
            console.error('Error saving food:', error);
            throw error; // Rialza l'errore per gestirlo nel chiamante
        }
    }

    
}

export const horsesDietService = new HorsesDietService();
